import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { Mode } from '@/common/entityFramework/enumerations/Mode';
import { useNavState } from '@/common/navState/NavStateContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { far, fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useCallback } from 'react';

/**
 * Per requirement GP-13, this provides a list of a reallocation's ratios
 */
export const ReallocRatioList = ({ calcObj }) => {
  const { navigateTo } = useNavState();

  const augRowsReq = useCallback(
    (rowsReq) => {
      if (!calcObj) return;
      rowsReq.expr = ExprUtils.and(rowsReq.expr, ExprUtils.whereParams('calcId', calcObj.id));
    },
    [calcObj.id],
  );

  const augFieldDefs = useCallback(
    (fieldToFieldDef) => {
      if (!calcObj) return;
      let columns;
      if (calcObj.type === 'REALLOC_SPLIT') {
        columns = ['origNdc11', 'origWac', 'splitNdc11', 'splitWac', 'reallocRatio'];
      } else {
        columns = ['discPeriod', 'salePeriod', 'reallocDims', 'reallocKey', 'totalDisc', 'totalAmt', 'reallocRatio'];
      }

      Object.keys(fieldToFieldDef).forEach((colName) => {
        if (!columns.includes(colName)) {
          delete fieldToFieldDef[colName];
        } else {
          const index = columns.indexOf(colName);
          fieldToFieldDef[colName].precedence = (index + 1) * 100;
        }
      });
    },
    [calcObj.id],
  );

  //const getContextMenuItems = (params) => {
  //TODO: Add Trx Viewers with v6 ViewerPlugin
  //}

  const getContextMenuItems = (params) => {
    const data = params.node.data;

    let navState = {
      mode: Mode.DETAIL,
      entityId: data.id,
    };

    const contextMenuItems = [
      {
        name: 'Details',
        icon: fas.faInfoCircle,
        action: () => navigateTo('gp/calc-prod-res', navState),
      },
    ];

    const viewerReqParam = {
      code: 'REALLOC_TRX',
      data: data,
      calc: calcObj,
      ndcLevel: 'NDC11',
    };

    const navigateToTrxs = (trxSrcCode, orig) =>
      navigateTo('gp/realloc-viewer', {
        viewerReqParam: {
          ...viewerReqParam,
          trxSrcCode,
          orig,
          ndc11:
            calcObj.type === 'REALLOC_SPLIT' ?
              orig ? data.origNdc11
              : data.splitNdc11
            : null,
        },
        entity: 'REALLOC_RATIO',
      });

    let trxsButton = {};
    if (calcObj.type === 'REALLOC_SPLIT') {
      trxsButton = {
        name: 'Original Transactions',
        icon: fas.faCalculator,
        subMenu: [
          {
            name: 'Direct Sale Transactions',
            icon: fas.faFileInvoice,
            action: () => navigateToTrxs('DS', true),
          },
          {
            name: 'Indirect Sale Transactions',
            icon: fas.faClipboard,
            action: () => navigateToTrxs('IS', true),
          },
          {
            name: 'Rebate Transactions',
            icon: far.faListUl,
            action: () => navigateToTrxs('RB', true),
          },
        ],
      };
    } else {
      trxsButton = {
        name: 'Original Transactions',
        icon: fas.faCalculator,
        action: () => navigateToTrxs('RB', true),
      };
    }

    contextMenuItems.push(trxsButton);

    let reallocTrxsButton = {};
    if (calcObj.type === 'REALLOC_SPLIT') {
      reallocTrxsButton = {
        name: 'Reallocated Transactions',
        icon: fas.faRotate,
        subMenu: [
          {
            name: 'Direct Sale Transactions',
            icon: fas.faFileInvoice,
            action: () => navigateToTrxs('DS', false),
          },
          {
            name: 'Indirect Sale Transactions',
            icon: fas.faClipboard,
            action: () => navigateToTrxs('IS', false),
          },
          {
            name: 'Rebate Transactions',
            icon: far.faListUl,
            action: () => navigateToTrxs('RB', false),
          },
        ],
      };
    } else {
      reallocTrxsButton = {
        name: 'Reallocated Transactions',
        icon: fas.faRotate,
        action: () => navigateToTrxs('RB', false),
      };
    }

    contextMenuItems.push(reallocTrxsButton);

    return contextMenuItems;
  };

  return (
    <EntityList
      entity="REALLOC_RATIO"
      augRowsReq={augRowsReq}
      augFieldDefs={augFieldDefs}
      gridOptions={{
        getContextMenuItems,
      }}
    />
  );
};
